/**
 * RegulatoryBasisDrawer Component (DC-1063)
 * ============================================================================
 * Shows the regulatory basis for a specific finding/metric. Links the
 * requirement to the actual assessment result with compliance status.
 *
 * Used by AlcoaSummaryCard and FDACredibilityCard to show the regulatory
 * background behind each principle/dimension score.
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import type { RegulatoryRequirement, RegulatoryCitation } from '../../types/evidence';
import { LineageIndicator } from '../value-drivers/LineageIndicator';
import type { StationId } from '../value-drivers/LineageIndicator';
import './regulatory-basis-drawer.css';

// ============================================================================
// Types
// ============================================================================

export interface EducationalMetric {
  /** Metric name (e.g., "Attributable", "Relevance") */
  name: string;
  /** Metric score (0-100) */
  score: number;
  /** Compliance status */
  status: 'compliant' | 'partial' | 'non_compliant' | 'not_assessed';
  /** Description from the assessment */
  description?: string;
  /** Finding details from the assessment */
  findings?: string[];
  /** Recommendations from the assessment */
  recommendations?: string[];
}

/**
 * Full-chain lineage context for a principle, shown in the "Evidence Lineage"
 * section: how the score was derived (component breakdown), which upstream
 * stations it consumed, and the persisted provenance record. Optional — only
 * ALCOA+ principle drawers pass it.
 */
export interface PrincipleLineage {
  /** evidence (counts toward headline) | indicator | not_assessable */
  basis: 'evidence' | 'indicator' | 'not_assessable';
  /** Component sub-scores that produced the principle score (label → 0-100) */
  componentScores?: Record<string, number>;
  /** Upstream stations whose results this principle consumed */
  sourceStations?: StationId[];
  /** Persisted provenance record for the assessment */
  provenance?: {
    eventId?: string;
    assessedAt?: string;
    assessorVersion?: string;
  };
}

export interface RegulatoryBasisDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** The metric being explained */
  metric: EducationalMetric | null;
  /** The regulatory requirement this metric maps to */
  requirement?: RegulatoryRequirement | null;
  /** Loading state while fetching requirement */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Full-chain lineage for this principle (ALCOA+ only); shows derivation,
   *  contributing stations, and the persisted provenance record. */
  lineage?: PrincipleLineage | null;
}

// ============================================================================
// Basis config (evidence / indicator / not_assessable)
// ============================================================================

const BASIS_CONFIG: Record<PrincipleLineage['basis'], { label: string; note: string; className: string }> = {
  evidence: {
    label: 'Evidence-backed',
    note: 'Scored from analysis of real data values or consumed station metrics. Contributes to the headline ALCOA+ score.',
    className: 'regulatory-basis__basis--evidence',
  },
  indicator: {
    label: 'Indicator (dataset-level)',
    note: 'Scored from dataset structure heuristics (e.g. column-name presence). A readiness signal that cannot independently evidence a system-level control — confirming it requires source-system evidence (audit trails, e-signatures, chain of custody). Excluded from the headline score.',
    className: 'regulatory-basis__basis--indicator',
  },
  not_assessable: {
    label: 'Not assessable from dataset',
    note: 'Could not be scored from the available evidence (e.g. the required upstream station has not been run). Excluded from the headline score.',
    className: 'regulatory-basis__basis--not-assessable',
  },
};

/** Humanize a component key, e.g. "userAttribution" → "User attribution". */
function humanizeComponent(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

// ============================================================================
// Helpers
// ============================================================================

function getStatusClass(status: EducationalMetric['status']): string {
  switch (status) {
    case 'compliant': return 'regulatory-basis__status--compliant';
    case 'partial': return 'regulatory-basis__status--partial';
    case 'non_compliant': return 'regulatory-basis__status--non-compliant';
    default: return 'regulatory-basis__status--not-assessed';
  }
}

function getStatusLabel(status: EducationalMetric['status']): string {
  switch (status) {
    case 'compliant': return 'Compliant';
    case 'partial': return 'Partial Compliance';
    case 'non_compliant': return 'Non-Compliant';
    default: return 'Not Assessed';
  }
}

// ============================================================================
// Component
// ============================================================================

export const RegulatoryBasisDrawer: React.FC<RegulatoryBasisDrawerProps> = ({
  isOpen,
  onClose,
  metric,
  requirement,
  loading = false,
  error = null,
  lineage = null,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showAllCitations, setShowAllCitations] = useState(false);

  // ============================================================================
  // Effects
  // ============================================================================

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setShowAllCitations(false);
    }
  }, [isOpen]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderCitation = (citation: RegulatoryCitation, index: number) => (
    <div key={index} className="regulatory-basis__citation">
      <div className="regulatory-basis__citation-header">
        <span className="regulatory-basis__citation-framework">{citation.framework}</span>
        <span className="regulatory-basis__citation-document">{citation.document}</span>
      </div>
      <p className="regulatory-basis__citation-section">{citation.section}</p>
      <blockquote className="regulatory-basis__citation-text">
        "{citation.text}"
      </blockquote>
      <a
        href={citation.url}
        target="_blank"
        rel="noopener noreferrer"
        className="regulatory-basis__citation-link"
      >
        View Source Document
      </a>
    </div>
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen || !metric) return null;

  const citations = requirement?.citations || [];
  const visibleCitations = showAllCitations ? citations : citations.slice(0, 2);
  const hasMoreCitations = citations.length > 2;

  return (
    <>
      {/* Overlay */}
      <div
        className="regulatory-basis__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="regulatory-basis__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="regulatory-basis-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="regulatory-basis__header">
          <div className="regulatory-basis__header-content">
            <h2 id="regulatory-basis-title" className="regulatory-basis__title">
              Regulatory Basis
            </h2>
            <span className="regulatory-basis__metric-name">{metric.name}</span>
          </div>
          <button
            type="button"
            className="regulatory-basis__close-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="regulatory-basis__content">
          {loading ? (
            <div className="regulatory-basis__loading">
              <div className="regulatory-basis__loading-spinner" />
              <p>Loading regulatory basis...</p>
            </div>
          ) : error ? (
            <div className="regulatory-basis__error">
              <p>{error}</p>
            </div>
          ) : (
            <>
              {/* Assessment Result Section */}
              <section className="regulatory-basis__section">
                <h3 className="regulatory-basis__section-title">Assessment Result</h3>
                <div className="regulatory-basis__result">
                  <div className="regulatory-basis__score-row">
                    <div className="regulatory-basis__score">
                      <span className="regulatory-basis__score-value">{metric.score}%</span>
                      <span className="regulatory-basis__score-label">Score</span>
                    </div>
                    <span className={`regulatory-basis__status ${getStatusClass(metric.status)}`}>
                      {getStatusLabel(metric.status)}
                    </span>
                  </div>
                  {metric.description && (
                    <p className="regulatory-basis__result-description">{metric.description}</p>
                  )}
                </div>
              </section>

              {/* Evidence Lineage Section (ALCOA+) — how the score was derived,
                  what it consumed, and the persisted provenance record. */}
              {lineage && (
                <section className="regulatory-basis__section regulatory-basis__lineage">
                  <h3 className="regulatory-basis__section-title">Evidence Lineage</h3>

                  {/* Basis */}
                  <div className={`regulatory-basis__basis ${BASIS_CONFIG[lineage.basis].className}`}>
                    <span className="regulatory-basis__basis-label">{BASIS_CONFIG[lineage.basis].label}</span>
                    <p className="regulatory-basis__basis-note">{BASIS_CONFIG[lineage.basis].note}</p>
                  </div>

                  {/* Component-score derivation */}
                  {lineage.componentScores && Object.keys(lineage.componentScores).length > 0 && (
                    <div className="regulatory-basis__components">
                      <h4>How this score was derived</h4>
                      <ul className="regulatory-basis__component-list">
                        {Object.entries(lineage.componentScores).map(([key, value]) => (
                          <li key={key} className="regulatory-basis__component">
                            <span className="regulatory-basis__component-label">{humanizeComponent(key)}</span>
                            <span className="regulatory-basis__component-bar">
                              <span
                                className="regulatory-basis__component-bar-fill"
                                style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
                              />
                            </span>
                            <span className="regulatory-basis__component-score">{Math.round(value)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Contributing source stations */}
                  {lineage.sourceStations && lineage.sourceStations.length > 0 ? (
                    <div className="regulatory-basis__stations">
                      <h4>Contributing evidence</h4>
                      <LineageIndicator
                        sourceStations={lineage.sourceStations}
                        mode="expanded"
                        size="small"
                      />
                    </div>
                  ) : (
                    <p className="regulatory-basis__stations-empty">
                      Derived from the dataset's own structure — no upstream station was consumed.
                    </p>
                  )}

                  {/* Provenance record */}
                  {lineage.provenance && (lineage.provenance.eventId || lineage.provenance.assessedAt) && (
                    <div className="regulatory-basis__provenance">
                      <h4>Provenance record</h4>
                      <dl className="regulatory-basis__provenance-grid">
                        {lineage.provenance.eventId && (
                          <>
                            <dt>Lineage event</dt>
                            <dd><code>{lineage.provenance.eventId}</code></dd>
                          </>
                        )}
                        {lineage.provenance.assessorVersion && (
                          <>
                            <dt>Assessor version</dt>
                            <dd>{lineage.provenance.assessorVersion}</dd>
                          </>
                        )}
                        {lineage.provenance.assessedAt && (
                          <>
                            <dt>Assessed</dt>
                            <dd>{lineage.provenance.assessedAt}</dd>
                          </>
                        )}
                      </dl>
                    </div>
                  )}
                </section>
              )}

              {/* Findings Section */}
              {metric.findings && metric.findings.length > 0 && (
                <section className="regulatory-basis__section">
                  <h3 className="regulatory-basis__section-title">Key Findings</h3>
                  <ul className="regulatory-basis__findings-list">
                    {metric.findings.map((finding, i) => (
                      <li key={i}>{finding}</li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Regulatory Requirement Section */}
              {requirement && (
                <section className="regulatory-basis__section">
                  <h3 className="regulatory-basis__section-title">Regulatory Requirement</h3>
                  <div className="regulatory-basis__requirement">
                    <h4 className="regulatory-basis__requirement-principle">{requirement.principle}</h4>
                    <p className="regulatory-basis__requirement-text">{requirement.requirement}</p>
                    <div className="regulatory-basis__requirement-meta">
                      <span className="regulatory-basis__requirement-category">
                        {requirement.category === 'alcoa' ? 'ALCOA+ Data Integrity' : 'FDA AI/ML Credibility'}
                      </span>
                      <span className="regulatory-basis__requirement-frameworks">
                        {requirement.frameworks.join(', ')}
                      </span>
                    </div>
                  </div>

                  {/* Rationale */}
                  <div className="regulatory-basis__rationale">
                    <h4>Why This Matters</h4>
                    <p>{requirement.rationale}</p>
                  </div>

                  {/* Compliance Thresholds */}
                  <div className="regulatory-basis__thresholds">
                    <h4>Compliance Thresholds</h4>
                    <div className="regulatory-basis__thresholds-grid">
                      <div className="regulatory-basis__threshold regulatory-basis__threshold--compliant">
                        <span className="regulatory-basis__threshold-label">Compliant</span>
                        <span className="regulatory-basis__threshold-value">≥{requirement.thresholds.compliant}%</span>
                      </div>
                      <div className="regulatory-basis__threshold regulatory-basis__threshold--partial">
                        <span className="regulatory-basis__threshold-label">Partial</span>
                        <span className="regulatory-basis__threshold-value">≥{requirement.thresholds.partial}%</span>
                      </div>
                      <div className="regulatory-basis__threshold regulatory-basis__threshold--non">
                        <span className="regulatory-basis__threshold-label">Non-Compliant</span>
                        <span className="regulatory-basis__threshold-value">&lt;{requirement.thresholds.partial}%</span>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Citations Section */}
              {citations.length > 0 && (
                <section className="regulatory-basis__section">
                  <h3 className="regulatory-basis__section-title">
                    Regulatory Citations ({citations.length})
                  </h3>
                  <div className="regulatory-basis__citations">
                    {visibleCitations.map((citation, i) => renderCitation(citation, i))}
                  </div>
                  {hasMoreCitations && (
                    <button
                      className="regulatory-basis__show-more"
                      onClick={() => setShowAllCitations(!showAllCitations)}
                    >
                      {showAllCitations
                        ? 'Show fewer citations'
                        : `Show ${citations.length - 2} more citation${citations.length - 2 !== 1 ? 's' : ''}`
                      }
                    </button>
                  )}
                </section>
              )}

              {/* Recommendations Section */}
              {metric.recommendations && metric.recommendations.length > 0 && (
                <section className="regulatory-basis__section">
                  <h3 className="regulatory-basis__section-title">Recommendations</h3>
                  <ul className="regulatory-basis__recommendations-list">
                    {metric.recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="regulatory-basis__footer">
          <a
            href="/settings/governance/evidence-library"
            className="regulatory-basis__footer-link"
          >
            View Full Evidence Library
          </a>
          <button
            type="button"
            className="regulatory-basis__footer-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default RegulatoryBasisDrawer;
